import React from 'react';
import { TableCell, Button, useTheme, useMediaQuery, Box, CircularProgress } from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';
import { Step, StepAction } from '../../types/Step';
import { getButtonStyles, nameDateStyle } from './StepItemStyles';
import { humanizeTimestamp } from '../../utility/dateUtils';
import { taskBaseColor } from '../../utility/task';
import { TaskStatus } from '../../types/Task';

const IN_PROGRESS_COLOR = taskBaseColor(TaskStatus.IN_PROGRESS);
export const COMPLETE_COLOR = '#35B23D';
interface StepRowProps {
  step: Step;
  handleStepToggle: (step: Step, action: StepAction) => void;
  editPermitted: boolean;
  taskUuid: string;
  loadingStep: { uuid: string; action: StepAction } | null;
}

export const StepItem: React.FC<StepRowProps> = ({ step, handleStepToggle, editPermitted, taskUuid, loadingStep }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const stepsButtonStyles = {
    '& .MuiButton-startIcon': { marginRight: `${isMobile ? '0' : '8px'}` },
    width: { xs: 'fit-content', sm: '100%' },
  };
  const isStartLoading = loadingStep?.uuid === step.uuid && loadingStep?.action === StepAction.START;
  const isCompleteLoading = loadingStep?.uuid === step.uuid && loadingStep?.action === StepAction.COMPLETE;

  return (
    <>
      <TableCell width={'40%'}>{step.name}</TableCell>
      <TableCell width={'30%'}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {taskUuid !== 'new' && (
            <Button
              startIcon={
                isStartLoading ? <CircularProgress size={16} /> : <CheckCircleOutline sx={{ color: step.started_at ? IN_PROGRESS_COLOR : undefined }} />
              }
              variant="contained"
              onClick={() => handleStepToggle(step, StepAction.START)}
              disabled={!editPermitted || isStartLoading}
              style={{
                ...getButtonStyles(!!step.started_at),
                backgroundColor: step.started_at ? `${IN_PROGRESS_COLOR}15` : '#fff',
                border: `1px solid ${step.started_at ? IN_PROGRESS_COLOR : '#E3E3E5'}`,
                display: `${step.completed_at ? 'none' : 'flex'}`,
              }}
              sx={stepsButtonStyles}
            >
              {!isMobile && (step.started_at ? 'In Progress' : 'Mark Start')}
            </Button>
          )}
          <Box sx={nameDateStyle}>
            {step.started_by_name && <Box>{step.started_by_name}</Box>}
            {step.started_at && <Box>{humanizeTimestamp(step.started_at)}</Box>}
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {taskUuid !== 'new' && (
            <Button
              startIcon={isCompleteLoading ? <CircularProgress size={16} /> : <CheckCircleOutline sx={{ color: `${step.completed_at && COMPLETE_COLOR}` }} />}
              variant="contained"
              onClick={() => handleStepToggle(step, StepAction.COMPLETE)}
              disabled={!editPermitted || isCompleteLoading}
              style={{
                ...getButtonStyles(!!step.completed_at),
                backgroundColor: step.completed_at ? '#E8F5E9' : '#fff',
                border: `1px solid ${step.completed_at ? COMPLETE_COLOR : '#E3E3E5'}`,
              }}
              sx={stepsButtonStyles}
            >
              {!isMobile && (step.completed_at ? 'Completed' : 'Mark Completed')}
            </Button>
          )}
          <Box sx={nameDateStyle}>
            {step.completed_by_name && <Box>{step.completed_by_name}</Box>}
            {step.completed_at && <Box>{humanizeTimestamp(step.completed_at)}</Box>}
          </Box>
        </Box>
      </TableCell>
    </>
  );
};
